/* eslint-disable @typescript-eslint/no-unused-vars */
import { useModelViewerScript } from 'lib/utils/use-model-viewer-script';
import { useEffect, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { isDesktop } from 'react-device-detect';
import Headline from 'src/helpers/Headline/Headline';
import classNames from 'classnames';
import SvgIcon, { IconTypes } from '../SvgIcon/SvgIcon';
import { getEnum } from 'lib/utils/get-enum';
import ButtonSecondary from '../Button/buttons/btn--secondary';
import ButtonTertiary from '../Button/buttons/btn--tertiary';

export type ARProductModalProps = Feature.EnterpriseWeb.RenewalByAndersen.Cards.ARProductCard & {
  isModalOpen: boolean;
};

const ButtonSecondaryClasses = () => {
  return {
    btnClass:
      'group relative flex w-fit items-center border-2 border-theme-btn-secondary-border bg-theme-btn-secondary-bg p-[14px] font-serif text-button font-bold text-theme-btn-secondary-text hover:bg-theme-btn-secondary-bg-hover hover:text-theme-btn-secondary-text-hover disabled:bg-gray disabled:border-gray disabled:text-dark-gray disabled:cursor-not-allowed  w-[150px] h-[55px]',
    iconClass: 'ml-[10px] text-primary group-hover:text-primary group-disabled:text-dark-gray pr-2',
  };
};

export const ButtonTertiaryClasses = () => {
  return {
    btnClass:
      'group relative flex w-fit items-center font-serif text-button font-bold text-theme-text after:absolute after:bottom-[-4px] after:hidden after:h-[3px] after:w-full after:bg-theme-btn-icon-hover hover:after:inline-block disabled:text-gray pl-4',
    iconClass:
      'ml-[10px] text-theme-btn-icon group-hover:text-theme-btn-icon-hover group-hover:underline group-hover:decoration-theme-btn-icon-hover group-hover:underline-offset-8',
  };
};

const ARProductModal = (props: ARProductModalProps) => {
  const { fields } = props;
  // Add the Modal Viewer script
  useModelViewerScript();
  const [isARModalOpen, setIsModalOpen] = useState(props.isModalOpen);
  const [angle, setAngle] = useState(0);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const _icon = getEnum<IconTypes>(fields?.buttonIcon);

  const handleOrientationChange = () => {
    setAngle(window.screen.orientation.angle);
  };

  const handleOpen = () => {
    //todo: set fields?.primaryLink behavior
  };

  const handleClose = () => {
    setIsModalOpen(false);
    const arModalElement = document.getElementById('arProductModal');
    arModalElement?.classList.toggle('hidden');
  };
  useEffect(() => {
    if (screen.orientation) {
      setAngle(window.screen.orientation.angle);
      // Property doesn't exist on screen in IE11
      screen.orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  const desktopMessage = () => {
    return (
      <>
        <div className="bottom-[180px] mt-8 block p-4 font-sans text-[12px] leading-[17px] md:absolute">
          {fields?.desktopMessage?.value}
        </div>
      </>
    );
  };

  const mobileCTAs = () => {
    return (
      <>
        <div>
          <div className="flex justify-center">
            <div className="mt-s flex flex-row items-start items-center items-center justify-center gap-4">
              {fields?.buttonText && (
                <div className="">
                  <button
                    onClick={() => {
                      handleOpen();
                    }}
                    className={classNames(ButtonSecondaryClasses().btnClass)}
                  >
                    <SvgIcon icon={_icon} className={ButtonSecondaryClasses().iconClass} />
                    <span>{fields?.buttonText?.value}</span>
                  </button>
                </div>
              )}
              <div className="">
                <button
                  onClick={() => {
                    //Implement behavior
                  }}
                  className={classNames(
                    ButtonSecondaryClasses().btnClass,
                    'justify-center p-[16px]'
                  )}
                >
                  <SvgIcon
                    icon="share"
                    className={classNames(ButtonSecondaryClasses().iconClass)}
                  />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const productProperties = () => {
    return (
      <>
        <div className={classNames('mb-8 w-full columns-2 gap-2 pr-2 md:gap-4 md:pr-0')}>
          <div className=" flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Exterior Color</div>
            <div className="pl-[16px] text-dark-gray md:pl-[29px]">
              {fields?.exteriorColor?.value}
            </div>
          </div>
          <div className="mt-2 flex  pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Interior Color</div>
            <div className="pl-[12px] text-dark-gray md:pl-[32px]">
              {fields?.interiorColor?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] pr-[0.25rem] font-medium md:w-auto">Hardware Style</div>
            <div className="pl-[12px] text-dark-gray md:pl-[16px]">
              {fields?.hardwareStyle?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px]  leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Hardware Color</div>
            <div className="pl-[12px] text-dark-gray md:pl-[16px]">
              {fields?.hardwareColor?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Grille Style</div>
            <div className="pl-[8px] text-dark-gray md:pl-[32px]">{fields?.grilleStyle?.value}</div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Product Type</div>
            <div className="pl-2 text-dark-gray md:pl-4">{fields?.productType?.value}</div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="w-[70px] font-medium md:w-auto">Product Size</div>
            <div className="pl-[11px] text-dark-gray md:pl-[20px]">
              {fields?.productSize?.value}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <ModalWrapper
        isModalOpen={isARModalOpen}
        size={angle === 90 ? 'extra-large' : 'fluid'}
        handleClose={handleClose}
        modalLabel="arProductModal"
        customOverlayclass="flex"
      >
        <div className="flex flex-col md:w-[840px]">
          <div>
            {!isDesktop && (
              <div>
                <div className="flex flex-col px-4 pb-4">
                  <model-viewer
                    src={fields?.gltfLink?.value.href}
                    ios-src={fields?.usdzLink?.value.href}
                    ar=""
                    camera-controls=""
                    quick-look-browsers="safari chrome"
                    camera-orbit="0deg 90deg"
                    poster={fields?.posterImage?.value?.src}
                    tabindex="0"
                    class="mb-[15px] ml-auto mr-auto min-h-[350px] w-full self-center pr-8"
                    ar-status="not-presenting"
                  ></model-viewer>

                  <div className="flex flex-col">
                    <div className="flex flex-wrap">
                      <div className="mb-8 w-full"> {mobileCTAs()}</div>
                      <Headline
                        useTag="h2"
                        fields={{ headlineText: { value: fields?.productName?.value } }}
                        classes={classNames('text-theme-text text-sm-s font-medium mb-4')}
                      />
                      {productProperties()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            {isDesktop && (
              <div>
                <div className="flex flex-col px-4 pb-4 md:flex-row">
                  <model-viewer
                    src={fields?.gltfLink?.value.href}
                    ios-src={fields?.usdzLink?.value.href}
                    ar=""
                    camera-controls=""
                    quick-look-browsers="safari chrome"
                    camera-orbit="0deg 90deg"
                    poster={fields?.posterImage?.value?.src}
                    tabindex="0"
                    class="mb-[15px] ml-auto mr-auto min-h-[350px] w-full self-center pr-8 md:w-[650px]"
                    ar-status="not-presenting"
                  ></model-viewer>

                  <div className="flex flex-col">
                    <div className="flex flex-wrap  md:w-[462px]">
                      <Headline
                        useTag="h2"
                        fields={{ headlineText: { value: fields?.productName?.value } }}
                        classes={classNames('text-theme-text text-sm-s font-medium lg:text-m mb-s')}
                      />
                      {productProperties()}
                      {desktopMessage()}
                    </div>
                  </div>
                </div>
                <div className="ml-2 grid w-[800px] grid-cols-1 gap-4 p-4 ml:grid-cols-2">
                  <div className={classNames('flex w-[300px] flex-col items-start gap-2')}>
                    <div className=" text-sm-xs">
                      Provide your mobile number to receive the text link to start the augmented
                      reality experience.
                    </div>
                    <div className=" text-sm-xxs">Mobile Number</div>
                    <div className="flex flex-row">
                      <input
                        type="tel"
                        placeholder="xxx-xxx-xxxx"
                        className=" rounded-sm border border-[#b9b9b9]"
                        value={phone}
                        onChange={() => {
                          //update with functionality
                        }}
                      />
                      <button className={classNames(ButtonTertiaryClasses().btnClass)}>
                        <span>Submit</span>
                        <SvgIcon
                          icon="arrow"
                          className={classNames(ButtonTertiaryClasses().iconClass)}
                        />
                      </button>
                    </div>
                  </div>
                  {!emailSent ? (
                    <div
                      className={classNames(
                        'flex w-[300px] flex-col items-start gap-2',
                        isDesktop
                          ? ' border-l border-l-gray pl-[4rem]'
                          : ' mt-8 border-t border-t-gray pt-8'
                      )}
                    >
                      <div className=" text-sm-xs">
                        OR - You can provide your email address and we will send you the link to
                        your email.
                      </div>
                      <div className=" text-sm-xxs">Email address</div>
                      <div className="flex flex-row">
                        <input
                          type="email"
                          placeholder="name@email.com"
                          className=" rounded-sm border border-[#b9b9b9]"
                          value={email}
                          onChange={() => {
                            //update with functionality
                          }}
                        />
                        <button className={classNames(ButtonTertiaryClasses().btnClass)}>
                          <span>Submit</span>
                          <SvgIcon
                            icon="arrow"
                            className={classNames(ButtonTertiaryClasses().iconClass)}
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">Thank you for your interest!</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default ARProductModal;
